import {
  getCustomerInvoiceBalance,
  getCustomerInvoiceContactLabel,
  getCustomerInvoiceLabel,
  getCustomerReceivableInvoices,
  getCustomerInvoiceRowClass,
  CustomerInvoiceStatusPill,
  getCustomerInvoiceDaysPastDue,
} from '@/components/customers/customer-invoice-display'
import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { LoadingState } from '@/components/patterns/loading-state'
import { isHouseAccountEnabled } from '@/components/customers/HouseAccountSettingsSection'
import { PaymentForm } from '@/components/pos/PaymentForm'
import {
  CustomerLedgerHeaderCell,
  OrderHistoryMobileLabel,
} from '@/components/customers/CustomerDetailTabs'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  DetailCard,
  DetailIconBadge,
  DetailMetricWell,
  DetailWell,
  detailDataHeaderClass,
  detailDataListClass,
  detailInsetListClass,
  detailPaleIconClass,
  detailSurfaceActionClass,
} from '@/components/patterns/detail-page'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can, useAbility } from '@/lib/casl'
import { canViewCustomerReceivables } from '@/lib/customer-receivables-access'
import dayjs from '@/lib/dayjs'
import { CustomerPaymentTerms } from '@/lib/enums/CustomerPaymentTerms'
import {
  formatCardBrand,
  formatCardExpiry,
  formatCardLast4,
  isNonCardLink,
  SAVED_PAYMENT_METHOD_AUTHORIZATION_COPY,
} from '@/lib/payment-method-display'
import { getCustomerReceivablesTone } from '@/lib/pos/customer-receivables'
import {
  useCreateCustomerPaymentMethodSetupIntentMutation,
  useCustomerPaymentMethods,
  useDeleteCustomerPaymentMethodMutation,
  useStoreCustomerPaymentMethodMutation,
} from '@/lib/services/customers/customers.query'
import { cn, formatCalendarDate, formatCurrency, getErrorMessage, parseCurrency } from '@/lib/utils'
import { Link, useParams } from '@tanstack/react-router'
import {
  ArrowRight,
  AlertTriangle,
  BadgePercent,
  Banknote,
  Building2,
  CalendarDays,
  CircleDollarSign,
  CreditCard,
  Link2,
  FileClock,
  FileText,
  Loader2,
  Mail,
  Pencil,
  Plus,
  ReceiptText,
  Truck,
  Trash2,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

function formatPaymentTerms(customer) {
  if (
    customer?.pay_later_allowed === false ||
    customer?.allow_fulfillment_before_payment !== true
  ) {
    return 'Due on receipt'
  }

  if (!customer?.payment_terms_code) {
    return 'Due on receipt'
  }

  if (customer.payment_terms_code === CustomerPaymentTerms.Custom.Value) {
    return customer.payment_terms_days ? `${customer.payment_terms_days} days` : 'Custom terms'
  }

  const matchingTerm = Object.values(CustomerPaymentTerms).find(
    (term) => term.Value === customer.payment_terms_code
  )

  return matchingTerm?.Name ?? customer.payment_terms_code.replaceAll('_', ' ')
}

const customerMetricToneConfig = {
  neutral: {
    tone: 'neutral',
    valueClassName: '',
  },
  warning: {
    tone: 'warning',
    valueClassName: 'text-status-warning-foreground dark:text-status-warning-foreground',
  },
  danger: {
    tone: 'destructive',
    valueClassName: 'text-status-destructive-foreground dark:text-status-destructive-foreground',
  },
}

function CustomerMetricWell({ label, value, detail, icon, tone = 'neutral' }) {
  const resolvedTone = tone
  const toneConfig = customerMetricToneConfig[resolvedTone] ?? customerMetricToneConfig.neutral

  return (
    <DetailMetricWell
      label={label}
      value={value}
      detail={detail}
      icon={icon}
      tone={toneConfig.tone}
      valueClassName={cn('text-2xl', toneConfig.valueClassName)}
      className="h-full px-4 py-4"
    />
  )
}

function BillingSettingPreview({ label, value, children, valueClassName, icon: Icon = null }) {
  return (
    <div className="flex min-w-0 items-start gap-3">
      {Icon ? <Icon className={cn(detailPaleIconClass, 'mt-0.5')} /> : null}
      <div className="min-w-0">
        <Typography variant="eyebrow" className="block whitespace-normal">
          {label}
        </Typography>
        <div className={cn('mt-1 min-h-6 break-words text-base/6 font-medium', valueClassName)}>
          {children ?? value}
        </div>
      </div>
    </div>
  )
}

function BillingYesNoBadge({ value }) {
  const enabled = value === true

  return (
    <Badge variant={enabled ? 'success' : 'muted'} className="h-6 px-2.5 py-0">
      {enabled ? 'Yes' : 'No'}
    </Badge>
  )
}

function resolveStatementEmail(customer) {
  return customer?.billing_email || customer?.primary_contact?.email || customer?.email || null
}

function formatCreditLimit(customer) {
  if (
    customer?.credit_limit === null ||
    customer?.credit_limit === undefined ||
    customer?.credit_limit === ''
  ) {
    return 'No limit'
  }

  return formatCurrency(parseCurrency(customer.credit_limit))
}

export function CustomerSavedCardRow({
  card,
  trailing = null,
  selected = false,
  disabled = false,
  onSelect = null,
  className,
}) {
  const brand = formatCardBrand(card)
  const last4 = formatCardLast4(card)
  const expiry = formatCardExpiry(card)
  const isLink = isNonCardLink(card)
  const MethodIcon = isLink ? Link2 : CreditCard
  const isInteractive = typeof onSelect === 'function'
  const label = `${brand}${last4 ? ` ending in ${last4}` : ''}`
  const usageText = subscriptionUsageText(card)
  const Component = isInteractive ? SelectableSurface : 'div'

  const selectCard = () => {
    if (!disabled) {
      onSelect?.(card.id)
    }
  }

  return (
    <Component
      {...(isInteractive
        ? {
            type: 'button',
            selected,
            disabled,
            appearance: 'bare',
            padding: 'none',
          }
        : {})}
      aria-label={isInteractive ? label : undefined}
      onClick={isInteractive ? selectCard : undefined}
      className={cn(
        'flex w-full min-w-0 items-center gap-3 px-3 py-3 text-left transition-colors',
        isInteractive && !disabled && 'hover:bg-muted/30',
        selected && 'bg-muted/40 data-[state=on]:bg-muted/40',
        disabled && 'cursor-not-allowed bg-muted/35 text-muted-foreground',
        className
      )}
    >
      <MethodIcon className={cn(detailPaleIconClass, 'shrink-0')} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold">{label}</div>
        <Typography variant="label" tone="muted" className="block">
          {expiry ? `Expires ${expiry}` : isLink ? 'Saved in Link' : 'No expiration shown'}
          {card.is_default ? ' - Default' : ''}
        </Typography>
        {usageText ? (
          <Typography variant="label" tone="muted" className="block">
            {usageText}
          </Typography>
        ) : null}
      </div>
      {trailing}
    </Component>
  )
}

function currentSubscriptionCount(card) {
  return Number(card?.subscription_usage?.current_count ?? card?.current_subscription_count ?? 0)
}

function subscriptionUsageText(card) {
  const count = currentSubscriptionCount(card)

  if (count <= 0) {
    return ''
  }

  return count === 1 ? 'Used by 1 current subscription' : `Used by ${count} current subscriptions`
}

function subscriptionRemovalInstruction(card) {
  const count = currentSubscriptionCount(card)

  if (count <= 0) {
    return ''
  }

  return count === 1
    ? 'Update that subscription before removing this card.'
    : 'Update those subscriptions before removing this card.'
}

function CustomerCardOnFileRow({ card, customerId }) {
  const deletePaymentMethodMutation = useDeleteCustomerPaymentMethodMutation()
  const brand = formatCardBrand(card)
  const last4 = formatCardLast4(card)
  const usageText = subscriptionUsageText(card)
  const isUsedBySubscriptions = currentSubscriptionCount(card) > 0

  const removeCard = async (paymentMethodId) => {
    if (isUsedBySubscriptions) {
      return
    }

    try {
      await deletePaymentMethodMutation.mutateAsync({
        customerId,
        paymentMethodId,
      })
      toast.success('Card removed')
    } catch {
      toast.error('Card remove failed')
    }
  }

  return (
    <CustomerSavedCardRow
      card={card}
      trailing={
        <Can do="edit" on="customers">
          <ConfirmationDialog
            variant="destructive"
            title="Remove card"
            description={
              usageText
                ? `${brand}${last4 ? ` ending in ${last4}` : ''} is ${usageText.toLowerCase()}. ${subscriptionRemovalInstruction(card)}`
                : 'Remove this saved card from the customer profile?'
            }
            confirmText="Remove"
            confirmDisabled={isUsedBySubscriptions}
            pendingText="Removing..."
            isPending={deletePaymentMethodMutation.isPending}
            onConfirm={() => removeCard(card.id)}
            trigger={
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={cn(detailSurfaceActionClass, 'shrink-0')}
                aria-label={last4 ? `Remove ${brand} ending in ${last4}` : `Remove ${brand}`}
                disabled={deletePaymentMethodMutation.isPending}
              >
                <Trash2 className="size-4" />
              </Button>
            }
          />
        </Can>
      }
    />
  )
}

function resolveStripeResourceId(resource) {
  if (!resource) {
    return ''
  }

  if (typeof resource === 'string') {
    return resource
  }

  return typeof resource.id === 'string' ? resource.id : ''
}

export function CustomerAddCardDialog({ customerId, open, onOpenChange, onCardSaved }) {
  const createSetupIntentMutation = useCreateCustomerPaymentMethodSetupIntentMutation()
  const storePaymentMethodMutation = useStoreCustomerPaymentMethodMutation()
  const [setupIntent, setSetupIntent] = useState(null)
  const [setupIntentRefreshKey, setSetupIntentRefreshKey] = useState(0)
  const [errorMessage, setErrorMessage] = useState(null)
  const [isSavingCard, setIsSavingCard] = useState(false)
  const createSetupIntent = createSetupIntentMutation.mutateAsync

  useEffect(() => {
    if (!open || !customerId) {
      return
    }

    let cancelled = false

    createSetupIntent({ customerId })
      .then((response) => {
        if (!cancelled) {
          if (
            response?.client_secret &&
            response?.setup_intent_id &&
            response?.publishable_key &&
            response?.stripe_account
          ) {
            setSetupIntent({ ...response, _customerId: customerId })
            setErrorMessage(null)
          } else {
            setErrorMessage('Unable to prepare the card form.')
          }
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setErrorMessage(getErrorMessage(error) || 'Unable to prepare the card form.')
        }
      })

    return () => {
      cancelled = true
    }
  }, [createSetupIntent, customerId, open, setupIntentRefreshKey])

  const resetDialogState = () => {
    setSetupIntent(null)
    setSetupIntentRefreshKey(0)
    setErrorMessage(null)
    setIsSavingCard(false)
  }

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen && (isSavingCard || storePaymentMethodMutation.isPending)) {
      return
    }

    if (!nextOpen) {
      resetDialogState()
    }

    onOpenChange(nextOpen)
  }

  const handleSubmitCardDetails = async ({ confirmSetup, paymentMethod }) => {
    setErrorMessage(null)
    setIsSavingCard(true)

    try {
      if (!setupIntent?.client_secret || !setupIntent?.setup_intent_id) {
        throw new Error('Secure card setup is still loading. Please try again.')
      }

      if (!confirmSetup) {
        throw new Error('Secure card setup is not ready. Please try again.')
      }

      const { error, setupIntent: confirmedSetupIntent } = await confirmSetup(
        setupIntent.client_secret
      )

      if (error) {
        throw new Error(error.message || 'Stripe could not save this card.')
      }

      if (!confirmedSetupIntent || confirmedSetupIntent.status !== 'succeeded') {
        throw new Error('Finish saving the card before adding it to this customer.')
      }

      const paymentMethodId =
        resolveStripeResourceId(confirmedSetupIntent.payment_method) || paymentMethod?.id || ''

      if (!paymentMethodId) {
        throw new Error('Stripe did not return a saved card.')
      }

      const response = await storePaymentMethodMutation.mutateAsync({
        customerId,
        setupIntentId: confirmedSetupIntent.id || setupIntent.setup_intent_id,
        paymentMethodId,
        customerAuthorizationConfirmed: true,
      })
      onCardSaved?.(response?.data ?? [], paymentMethodId)
      toast.success('Card saved')
      resetDialogState()
      onOpenChange(false)
    } catch (error) {
      setSetupIntent(null)
      setSetupIntentRefreshKey((current) => current + 1)
      throw new Error(getErrorMessage(error) || 'Card save failed.', { cause: error })
    } finally {
      setIsSavingCard(false)
    }
  }

  const canShowCardForm = Boolean(
    setupIntent?.client_secret && setupIntent?._customerId === customerId
  )
  const isPreparing = open && !canShowCardForm && !errorMessage

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="small" viewport="scroll">
        <DialogHeader>
          <DialogTitle>Add Card</DialogTitle>
          <DialogDescription>Save a card for future customer payments.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isPreparing ? <LoadingState label="Loading secure card form..." /> : null}

          {errorMessage ? (
            <Alert variant="destructive">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          ) : null}

          {canShowCardForm ? (
            <PaymentForm
              paymentBootstrap={{
                publishable_key: setupIntent.publishable_key,
                stripe_account: setupIntent.stripe_account,
              }}
              disabled={isSavingCard || storePaymentMethodMutation.isPending}
              confirmationMode="setup"
              onError={setErrorMessage}
              onSubmitCardDetails={handleSubmitCardDetails}
              submitButtonLabel="Save card"
              submittingButtonLabel="Saving..."
              requiredConfirmationLabel={SAVED_PAYMENT_METHOD_AUTHORIZATION_COPY}
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function CustomerCardsOnFileCard({ customer }) {
  const customerId = customer?.id
  const [isAddCardOpen, setIsAddCardOpen] = useState(false)
  const { data, isFetching } = useCustomerPaymentMethods(customerId, {
    enabled: Boolean(customerId),
  })
  const cards = Array.isArray(data) ? data : (data?.data ?? [])

  return (
    <DetailCard accent="payment" contentClassName="space-y-4 p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <DetailIconBadge icon={CreditCard} tone="periwinkle" />
          <div className="min-w-0">
            <Typography variant="cardTitle">Cards on file</Typography>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {isFetching ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : null}
          <Can do="edit" on="customers">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className={detailSurfaceActionClass}
              aria-label="Add card"
              onClick={() => setIsAddCardOpen(true)}
              disabled={!customerId}
            >
              <Plus className="size-4" />
            </Button>
          </Can>
        </div>
      </div>

      {cards.length === 0 ? (
        <DetailWell compact tone="periwinkle">
          <Typography variant="supporting" className="block">
            No saved cards yet.
          </Typography>
        </DetailWell>
      ) : (
        <div className={cn(detailInsetListClass, detailDataListClass)}>
          {cards.map((card) => (
            <CustomerCardOnFileRow key={card.id} card={card} customerId={customerId} />
          ))}
        </div>
      )}
      <CustomerAddCardDialog
        customerId={customerId}
        open={isAddCardOpen}
        onOpenChange={setIsAddCardOpen}
      />
    </DetailCard>
  )
}

export function CustomerSummaryStrip({ customer, receivables, metrics }) {
  const ability = useAbility()
  const canViewReceivables = canViewCustomerReceivables(ability)
  const receivablesSummary = receivables?.summary ?? {}
  const openInvoices = getCustomerReceivableInvoices(receivables)
  const openBalance = parseCurrency(receivablesSummary.open_balance_total ?? 0)
  const pastDueBalance = parseCurrency(receivablesSummary.overdue_balance_total ?? 0)
  const creditLimit =
    customer?.pay_later_allowed !== false && customer?.credit_limit != null
      ? parseCurrency(customer.credit_limit)
      : null
  const balanceTone = getCustomerReceivablesTone({
    openBalance,
    overdueBalance: pastDueBalance,
    creditLimit,
  })
  const totalOrders = Number(metrics.total_orders ?? 0)
  const lastActivity = metrics.last_activity_at
    ? dayjs(metrics.last_activity_at).format('MMM D, YYYY')
    : null
  const cards = [
    canViewReceivables && openBalance > 0
      ? {
          label: 'Open Balance',
          value: formatCurrency(openBalance),
          detail:
            openInvoices.length > 0
              ? `${openInvoices.length} ${openInvoices.length === 1 ? 'order' : 'orders'} with a balance`
              : 'Open balance on file',
          tone: balanceTone,
          icon: CircleDollarSign,
        }
      : null,
    canViewReceivables && pastDueBalance > 0
      ? {
          label: 'Past Due',
          value: formatCurrency(pastDueBalance),
          detail: 'Needs attention',
          tone: balanceTone,
          icon: AlertTriangle,
        }
      : null,
    canViewReceivables && creditLimit !== null
      ? {
          label: 'Credit Limit',
          value: formatCurrency(creditLimit),
          detail:
            openBalance > 0
              ? `${formatCurrency(Math.max(creditLimit - openBalance, 0))} available`
              : 'No open balance',
          icon: BadgePercent,
        }
      : null,
    totalOrders > 0
      ? {
          label: 'Total Orders',
          value: totalOrders,
          detail: lastActivity ? `Last order ${lastActivity}` : null,
          icon: ReceiptText,
        }
      : null,
  ].filter(Boolean)

  if (cards.length === 0) {
    return null
  }

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      {cards.map((card) => (
        <CustomerMetricWell
          key={card.label}
          label={card.label}
          value={card.value}
          detail={card.detail}
          icon={card.icon}
          tone={card.tone}
        />
      ))}
    </div>
  )
}

export function CustomerBillingSetupCard({ customer, onEditBillingSettings }) {
  const path = usePathHelper()
  const { orgId } = useParams({ strict: false })
  const payLaterAllowed = customer.pay_later_allowed !== false
  const allowFulfillmentBeforePayment = customer.allow_fulfillment_before_payment === true
  const isHouseAccount = isHouseAccountEnabled(payLaterAllowed, allowFulfillmentBeforePayment)
  const organizationId = customer?.organization_id ?? customer?.organization?.id ?? orgId
  const billingSettingsLink =
    organizationId && path.billing?.index
      ? path.billing.index().using({ orgId: organizationId }).link
      : null

  return (
    <DetailCard accent="payment" contentClassName="space-y-4 p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <DetailIconBadge icon={ReceiptText} tone="periwinkle" />
          <div className="min-w-0">
            <Typography variant="cardTitle">Billing Setup</Typography>
          </div>
        </div>
        <Can do="manage" on="customers" field="billing">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className={cn(detailSurfaceActionClass, 'shrink-0')}
            aria-label="Edit billing setup"
            onClick={onEditBillingSettings}
          >
            <Pencil className="size-4" />
          </Button>
        </Can>
      </div>

      <div className="grid gap-5">
        <DetailWell
          tone="periwinkle"
          className="space-y-4 px-4 py-4"
          role="group"
          aria-label="House Account"
        >
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Building2 className="size-4 shrink-0 text-chart-2" aria-hidden="true" />
            House Account
          </div>
          <Typography variant="supporting" wrap="pretty" className="block">
            House Accounts can place orders and receive pickup or delivery before payment is
            collected.
          </Typography>
          <div className="grid gap-4">
            <BillingSettingPreview
              label="Allow Pay Later Orders"
              valueClassName="flex items-center"
              icon={FileClock}
            >
              <BillingYesNoBadge value={payLaterAllowed} />
            </BillingSettingPreview>
            {payLaterAllowed ? (
              <>
                <BillingSettingPreview
                  label="Allow Fulfillment Before Payment"
                  valueClassName="flex items-center"
                  icon={Truck}
                >
                  <BillingYesNoBadge value={allowFulfillmentBeforePayment} />
                </BillingSettingPreview>
                <BillingSettingPreview
                  label="Credit Limit"
                  value={formatCreditLimit(customer)}
                  icon={CircleDollarSign}
                />
              </>
            ) : null}
            {isHouseAccount ? (
              <BillingSettingPreview
                label="Payment Terms"
                value={formatPaymentTerms(customer)}
                icon={CalendarDays}
              />
            ) : null}
          </div>
        </DetailWell>
        <BillingSettingPreview
          label="Tax Exempt"
          valueClassName="flex items-center"
          icon={BadgePercent}
        >
          <BillingYesNoBadge value={customer.tax_exempt === true} />
        </BillingSettingPreview>
        <BillingSettingPreview
          label="Billing Email"
          value={resolveStatementEmail(customer) || 'No billing email'}
          icon={Mail}
        />
      </div>
      {billingSettingsLink ? (
        <Can do="list" on="billing-settings">
          <div className="grid gap-1.5 pt-1">
            <Typography variant="supporting" className="block">
              Want future customers to start with different billing defaults?
            </Typography>
            <div className="text-sm/6 font-medium">
              <Link
                {...billingSettingsLink}
                className="inline-flex w-fit items-center gap-1.5 text-interaction underline-offset-4 hover:underline"
              >
                Manage default billing settings
                <ArrowRight
                  className="size-4 h-lh shrink-0 stroke-interaction"
                  aria-hidden="true"
                />
              </Link>
            </div>
          </div>
        </Can>
      ) : null}
    </DetailCard>
  )
}

export function CustomerUnpaidOrdersCard({ receivables, onOpenStatement }) {
  const ability = useAbility()
  const path = usePathHelper()
  const invoices = getCustomerReceivableInvoices(receivables)
  const hasPastDueInvoices = invoices.some((invoice) => getCustomerInvoiceDaysPastDue(invoice) > 0)

  if (!canViewCustomerReceivables(ability) || invoices.length === 0) {
    return null
  }

  return (
    <DetailCard accent="payment" contentClassName="space-y-4 p-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <DetailIconBadge icon={Banknote} tone={hasPastDueInvoices ? 'warning' : 'periwinkle'} />
            <div>
              <Typography variant="cardTitle">Orders with a balance</Typography>
            </div>
          </div>
          <Typography variant="supporting" className="mt-3 block">
            Amounts this customer still owes. Past-due orders appear at the top of the list.
          </Typography>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={hasPastDueInvoices ? 'warning' : 'outline'}>{invoices.length} open</Badge>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className={detailSurfaceActionClass}
            aria-label="View customer statement"
            onClick={onOpenStatement}
          >
            <FileText className="size-4" />
          </Button>
        </div>
      </div>

      <div className={detailInsetListClass}>
        <div
          className={cn(
            'hidden grid-cols-[minmax(10rem,1fr)_8rem_7rem_7.25rem] gap-4 px-4 py-2.5 md:grid',
            detailDataHeaderClass
          )}
        >
          <CustomerLedgerHeaderCell>Order</CustomerLedgerHeaderCell>
          <CustomerLedgerHeaderCell>Due</CustomerLedgerHeaderCell>
          <CustomerLedgerHeaderCell className="text-right">Balance</CustomerLedgerHeaderCell>
          <CustomerLedgerHeaderCell className="text-right">Status</CustomerLedgerHeaderCell>
        </div>
        <div className={detailDataListClass}>
          {invoices.map((invoice) => {
            const remainingTotal = getCustomerInvoiceBalance(invoice)
            const invoiceLabel = getCustomerInvoiceLabel(invoice)

            return (
              <div
                key={invoice.id}
                className={getCustomerInvoiceRowClass(
                  invoice,
                  'grid grid-cols-2 gap-x-4 gap-y-3 rounded-none border-y-0 border-r-0 px-4 py-3 text-sm first:rounded-t-none last:rounded-b-md md:grid-cols-[minmax(10rem,1fr)_8rem_7rem_7.25rem] md:items-center'
                )}
              >
                <div className="col-span-2 min-w-0 md:col-span-1">
                  <Link {...path.salesOrders.show(invoice.id).link}>
                    <Typography variant="labelStrong" interaction="hoverUnderline">
                      {invoiceLabel}
                    </Typography>
                  </Link>
                  <Typography variant="supporting" className="mt-1 block">
                    {getCustomerInvoiceContactLabel(invoice)}
                  </Typography>
                </div>
                <div className="min-w-0">
                  <OrderHistoryMobileLabel>Due</OrderHistoryMobileLabel>
                  <span className="whitespace-nowrap tabular-nums">
                    {formatCalendarDate(invoice.due_date, 'MMM D, YYYY', 'No due date')}
                  </span>
                </div>
                <div className="min-w-0 md:text-right">
                  <OrderHistoryMobileLabel>Balance</OrderHistoryMobileLabel>
                  <span className="font-semibold tabular-nums">
                    {formatCurrency(remainingTotal)}
                  </span>
                </div>
                <div className="col-span-2 flex items-start md:col-span-1 md:justify-end">
                  <CustomerInvoiceStatusPill invoice={invoice} />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </DetailCard>
  )
}
import { SelectableSurface } from '@/components/patterns/selectable-surface'
